import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Users, Zap, Leaf, MapPin, Heart, Send, Sun, TrendingUp } from "lucide-react";

export default function Community() {
  const { user, lang } = useApp();
  const isIT = lang === "it";
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [{ data: s }, { data: p }] = await Promise.all([
        api.get("/community/stats"),
        api.get("/community/feed"),
      ]);
      setStats(s); setPosts(p);
    } catch { /* noop */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const send = async () => {
    if (!message.trim() || sending) return;
    if (!user) { toast.error(isIT ? "Accedi per postare" : "Login to post"); return; }
    if (!user.is_pro) { toast.error(isIT ? "Solo Pro può postare" : "Only Pro can post"); return; }
    setSending(true);
    try {
      await api.post("/community/feed", { message: message.trim() });
      setMessage("");
      load();
      toast.success(isIT ? "Post pubblicato" : "Posted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSending(false); }
  };

  const like = async (id) => {
    try { await api.post(`/community/feed/${id}/like`); load(); }
    catch { /* need login */ }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg pb-16">
      <div className="max-w-5xl mx-auto px-6 pt-12">
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 label-eco mb-4">
            <Users size={12} /> Community · SolarControl
          </div>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-[#17301C] leading-[0.95]">
            {isIT ? "Insieme, contro" : "Together, against"} <br />
            <span className="italic font-light text-[#EAB308]">{isIT ? "la bolletta." : "the bill."}</span>
          </h1>
          <p className="text-lg text-[#3A4A3E] mt-6 max-w-2xl mx-auto">
            {isIT
              ? "Guarda in tempo reale quanti kWh sta producendo l'intera community e condividi la tua storia."
              : "See in real time how many kWh the whole community is producing and share your story."}
          </p>
        </div>

        {/* STATS */}
        {stats && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="community-stats">
            <StatBig icon={Users} label={isIT ? "Impianti attivi" : "Active plants"} value={stats.active_plants} accent />
            <StatBig icon={Zap} label={isIT ? "Prodotto oggi" : "Produced today"} value={`${stats.today_produced_kwh.toLocaleString()}`} unit="kWh" />
            <StatBig icon={Sun} label={isIT ? "Potenza totale" : "Total capacity"} value={`${(stats.total_capacity_kwp / 1000).toFixed(2)}`} unit="MW" />
            <StatBig icon={Leaf} label={isIT ? "CO₂ evitata oggi" : "CO₂ saved today"} value={`${stats.co2_saved_today_kg.toLocaleString()}`} unit="kg" />
          </motion.div>
        )}

        {/* TOP CITIES */}
        {stats?.top_cities?.length > 0 && (
          <div className="card-surface p-6 mb-10" data-testid="top-cities">
            <div className="label-eco mb-4 flex items-center gap-2"><MapPin size={13} /> {isIT ? "Città più solari" : "Sunniest cities"}</div>
            <div className="flex flex-wrap gap-3">
              {stats.top_cities.map((c, i) => (
                <div key={c.city} className={`px-4 py-2 rounded-full font-semibold text-sm ${
                  i === 0 ? "bg-[#EAB308] text-[#17301C]" : "bg-[#EAE7E0] text-[#17301C]"
                }`} data-testid={`top-city-${i}`}>
                  #{i + 1} {c.city} · <span className="font-mono-nums">{c.kwp} kWp</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* POST BOX */}
        {user?.is_pro && (
          <div className="card-surface p-5 mb-8" data-testid="community-post-box">
            <div className="label-eco mb-3 flex items-center gap-2"><TrendingUp size={12} /> {isIT ? "Condividi la tua esperienza" : "Share your story"}</div>
            <div className="flex gap-2">
              <input value={message} onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                data-testid="community-post-input"
                placeholder={isIT ? "Ho appena raggiunto..." : "I just reached..."}
                className="flex-1 px-4 py-2.5 rounded-lg border border-[#DDE0D8] text-sm focus:outline-none focus:border-[#17301C]"
                maxLength={500} />
              <button onClick={send} disabled={sending || !message.trim()} data-testid="community-post-btn"
                className="px-4 py-2.5 rounded-lg bg-[#EAB308] text-[#17301C] font-semibold hover:bg-[#CA9A04] disabled:opacity-50">
                <Send size={16} />
              </button>
            </div>
          </div>
        )}

        {/* FEED */}
        <div className="space-y-4">
          {posts.length === 0 ? (
            <div className="card-surface p-12 text-center text-[#6E7A71]">
              {isIT ? "Ancora nessun post. Sii il primo!" : "No posts yet. Be the first!"}
            </div>
          ) : posts.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="card-surface p-5" data-testid={`community-post-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-bold text-[#17301C]">{p.user_name}</div>
                  <div className="text-xs text-[#6E7A71] font-mono-nums">
                    {new Date(p.created_at).toLocaleString("it-IT")}
                    {p.plant_kwp && ` · ${p.plant_kwp} kWp · ${p.plant_location}`}
                  </div>
                </div>
                <button onClick={() => like(p.id)} data-testid={`like-${p.id}`}
                  className="flex items-center gap-1 px-3 py-1 rounded-full bg-[#EAE7E0] text-[#17301C] hover:bg-[#DDE0D8] text-xs font-bold transition-colors">
                  <Heart size={12} /> {p.likes || 0}
                </button>
              </div>
              <p className="text-[#3A4A3E] leading-relaxed">{p.message}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

const StatBig = ({ icon: Icon, label, value, unit, accent }) => (
  <div className={`p-5 rounded-2xl border ${accent ? "bg-[#17301C] text-white border-[#17301C]" : "bg-white border-[#DDE0D8]"}`}>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={14} className={accent ? "text-[#EAB308]" : "text-[#6E7A71]"} />
      <div className={`text-[10px] uppercase tracking-widest font-bold ${accent ? "text-[#EAB308]" : "text-[#6E7A71]"}`}>{label}</div>
    </div>
    <div className={`text-4xl font-black font-mono-nums ${accent ? "text-[#EAB308]" : "text-[#17301C]"}`}>
      {value}<span className="text-xs ml-1 opacity-80">{unit}</span>
    </div>
  </div>
);
